import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import {
  AlertTitle,
  Box,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { noop } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  AI_GENERATION_COMPLETION_STATUS,
  ARTIFACT_TYPES,
  INSIGHT_TABLE_SWITCH_VALUES,
} from "../../../constants";
import { artifactAtom } from "../../../stores/jotai/artifacts/artifacts";
import {
  baseAtom,
  getEntryById,
  selectInsight,
  setSelectedArtifactType,
  updateBaseFinding,
} from "../../../stores/jotai/base";
import { canvasDataAtom } from "../../../stores/jotai/canvas/canvasData";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import {
  aiInsightGenerationProgressAtom,
  clearAiInsightGenerationProgress,
} from "../../../stores/jotai/insights/aiInsightGenerationStatus";
import { findingsAtom } from "../../../stores/jotai/insights/findings";
import { interviewFindingsAtom } from "../../../stores/jotai/insights/interviewFindings";
import { useUpdateFinding } from "../../../tanstackQuery/queries/findings";
import IwAlert from "../../app/IwAlert";
import {
  INSIGHT_TABLE_SORT_CRITERIA,
  INSIGHT_TABLE_SORT_DIRECTION,
} from "../utils/constants";
import InsightDetailsRow from "./InsightDetailsRow";
import InsightArtifactTypeToggle from "../common/InsightArtifactTypeToggle";
import InsightDetailsSkeletion from "../skeleton/InsightDetailsSkeletion";
import { useGetMetaArtifacts } from "../../../tanstackQuery/queries/artifacts";

interface SortParamState {
  direction: "asc" | "desc";
  criteria: "insight" | "created_at" | "created_by";
}
function safeParse(str, fallback = {}) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

const sortByQoute = (insights, direction) =>
  insights.sort((insight1, insight2) => {
   let quote1 = safeParse(insight1.quote);
   let quote2 = safeParse(insight2.quote);
    if(quote1.source === undefined && quote2.source===undefined){
       quote1 = insight1.metaInsightOrder;
       quote2 = insight2.metaInsightOrder
    }
    else if (quote1.source === "transcript" && quote2.source === "transcript") {
      quote1 = quote1?.audioBegin;
      quote2 = quote2?.audioBegin;
    } else {
      quote1 = quote1?.selection?.anchor;
      quote2 = quote2?.selection?.anchor;
    }

    return quote1 - quote2;
  });

const InsightDetails = (props) => {
  const {
    interviewID,
    selectedArtifactType,
    findingLinksData,
    isSuccessFindings,
    isFindingLoading,
    isFetching,
  } = props;
  const [sortParam, setSortParam] = useState<SortParamState>({
    direction: INSIGHT_TABLE_SORT_DIRECTION.ASC,
    criteria: INSIGHT_TABLE_SORT_CRITERIA.INSIGHT,
  });
  const [base, setBase] = useAtom(baseAtom);
  const [findings, setFindings] = useAtom(findingsAtom);
  const [interviewFindings] = useAtom(interviewFindingsAtom);
  const [isFetchingTriggered, setIsFetchingTriggered] = useState(false);
  const [canvasData, setCanvasData] = useAtom(canvasDataAtom);
  const [artifacts] = useAtom(artifactAtom);
  const [selectedTab, setSelectedTab] = useState([]);
  const [showInfoAlert, setShowInfoAlert] = useState(false);
  const isBaseLoading = Object.keys(base.user || {})?.length === 0;
  const discoverySession = useAtomValue(discoverySessionAtom);
  const selectedFinding = base.selectedFinding?.findingID;
  const [
    aiInsightGenerationProgressState,
    setAiInsightGenerationProgressState,
  ] = useAtom(aiInsightGenerationProgressAtom);

  const { keycloak } = useKeycloak();
  const artifactType =
    base?.selectedArtifactType.includes("transcription") 
      ? ARTIFACT_TYPES.TRANSCRIPT
      : ARTIFACT_TYPES.INTERVIEW_NOTE;
  const filteredInsights =
    interviewFindings?.[interviewID]?.length > 0
      ? interviewFindings?.[interviewID].filter((item) => {
          const findingID = findings[item]?.findingID;

          let quoteValue;
          try {
            quoteValue = JSON.parse(findings?.[item]?.quote);
          } catch (e) {
            quoteValue = null;
          }

          const isFindingFromType = {
            note: quoteValue?.source === "note",
            transcript: quoteValue?.source === "transcript",
            metaAnalysis:
              quoteValue?.source !== "note" &&
              quoteValue?.source !== "transcript",
          };

          const showTabs = {
            note: selectedTab?.includes(INSIGHT_TABLE_SWITCH_VALUES.NOTES),
            transcript: selectedTab?.includes(
              INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT
            ),
            metaAnalysis: selectedTab?.includes(
              INSIGHT_TABLE_SWITCH_VALUES.ANALYSIS
            ),
          };

          // 🔍 Get all childFindingIDs from analysis insights
          const allAnalysisFindings = Object.values(findings).filter(
            (f) => f.childFindingIDs?.length
          );
          const allChildFindingIDs = new Set(
            allAnalysisFindings.flatMap((f) => f.childFindingIDs)
          );

          // 🧠 Always exclude transcripts that are child findings of any analysis
          if (
            showTabs.transcript &&
            isFindingFromType.transcript &&
            allChildFindingIDs.has(findingID)
          ) {
            return false;
          }

          // 🔁 Generic rendering logic for visible types
          const visibleTabs = Object.entries(showTabs)
            .filter(([, value]) => value)
            .map(([key]) => key as keyof typeof isFindingFromType);

          if (visibleTabs.length === 3) return true;

          if (visibleTabs.length === 1) {
            return isFindingFromType[visibleTabs[0]];
          }

          if (visibleTabs.length === 2) {
            const hiddenType = Object.keys(isFindingFromType).find(
              (type) => !showTabs[type as keyof typeof showTabs]
            );
            return !isFindingFromType[
              hiddenType as keyof typeof isFindingFromType
            ];
          }

          return false;
        })
      : [];

  const client = useQueryClient();
  const { mutateAsync: updateFinding } = useUpdateFinding(
    { accessToken: keycloak.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["insights"] });
      },
    }
  );
  
  const metaAnalysis = artifacts?.metaAnalysis?.[interviewID];
  const customMetaArtifactData = client.getQueryData(["meta-artifacts", { artifactID: metaAnalysis?.artifactID, isMappedByParent: true }]);
  const {
      data: metaArtifactData,
      isLoading : isMetaDetailsLoading
    } = useGetMetaArtifacts(
      { accessToken: keycloak.token },
      { artifactID: metaAnalysis?.artifactID, isMappedByParent: true },
      {
        enabled: !customMetaArtifactData && !!metaAnalysis?.artifactID, 
        retry: 1,
        retryDelay: 3000,
      }
    );

  const sortedInsights = useMemo(() => {
    const { direction, criteria } = sortParam;
    let sortedResult = [...filteredInsights];
    const _sortedResult = sortedResult?.map((item) => findings?.[item]);
    
    sortByQoute(_sortedResult, direction);

    return _sortedResult;
  }, [filteredInsights, sortParam]);

  const buildHypoSupporters = () => {
    if (base?.selectedFinding === undefined) return [];

    let hypothesisLinks = [];

    for (let i = 0; i < findingLinksData?.length; i++) {
      const __link = findingLinksData[i];

      if (__link.findingID === base.selectedFinding.findingID) {
        let foundCategoryID = undefined;

        try {
          let matchingEntry = getEntryById(canvasData, __link?.entryID);
          //@ts-ignore
          foundCategoryID = matchingEntry?.categoryID;
        } catch (e) {}

        hypothesisLinks.push({
          hypothesisKey: __link?.entryID,
          supports: __link?.supports,
          category: foundCategoryID,
          key: __link?.linkID,
        });
      }
    }
    return hypothesisLinks;
  };

  const onAccept = (insight, onSuccess = noop, onFail = noop) => {
    const hypothesisLinks = buildHypoSupporters();
    const joineries = insight?.joinery
      ?.map((joinery) => joinery.customFindingTypeID)
      .join(",");
    const insightPayload = {
      editor: "material",
      artifactID: insight?.artifactID,
      audioBegin: insight?.audioBegin,
      audioEnd: insight?.audioEnd,
      findingID: insight?.findingID,
      comment: insight?.comment,
      created: insight?.created,
      typeID: joineries,
      quote: JSON.parse(insight?.quote),
      hypothesisLinks: JSON.stringify(hypothesisLinks),
      interviewID: insight?.interviewID,
      rating: insight?.rating?.global?.rating,
      isDraft: false,
      sessionID: discoverySession?.sessionID,
    };

    updateFinding(insightPayload)
      .then((value) => {
        onSuccess();
        updateBaseFinding(setBase, value);
      })
      .catch((error) => {
        onFail(error);
      });
  };

  const onCloseProgressAlert = (isSuccess?: boolean) => {
    if (isSuccess) {
      setShowInfoAlert(true);
      return;
    }
    clearAiInsightGenerationProgress(
      setAiInsightGenerationProgressState,
      artifactType,
      interviewID
    );
    setShowInfoAlert(false);
  };

  const handleToggle = (
    event: React.MouseEvent<HTMLElement>,
    newSelected: string[]
  ) => {
    if (newSelected?.length > 0) {
      setSelectedTab(newSelected);
    }
  };

  useEffect(() => {
    if (showInfoAlert) {
      setTimeout(() => {
        onCloseProgressAlert();
      }, 2000);
    }
  }, [showInfoAlert]);

  useEffect(() => {
    if (
      aiInsightGenerationProgressState[artifactType][interviewID] ===
      AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
    ) {
      client.invalidateQueries({ queryKey: ["meta-artifacts"] });
      setTimeout(() => {
        onCloseProgressAlert();
      }, 5000);
    }
  }, [aiInsightGenerationProgressState[artifactType][interviewID]]);

  useEffect(() => {
    if (isFetching && !isFetchingTriggered) {
      setIsFetchingTriggered(true);

      setTimeout(() => {
        setIsFetchingTriggered(false);
      }, 1500);
    }
  }, [isFetching, isFetchingTriggered]);
  useEffect(() => {
    if (selectedArtifactType.includes("notes")) {
      setSelectedTab([
        INSIGHT_TABLE_SWITCH_VALUES.NOTES,
        INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT,
      ]);
    } else if(selectedArtifactType.includes("metaanalysis")){
      setSelectedTab([
        INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT,
        INSIGHT_TABLE_SWITCH_VALUES.ANALYSIS
      ]);
    }
  }, [selectedArtifactType]);

  const renderInsightProgressAlert = () => {
    const progressByInterviewID =
      aiInsightGenerationProgressState[artifactType][interviewID];
    let alertByProgress = null;
    if (!progressByInterviewID) {
      return null;
    }

    if (showInfoAlert) {
      const insightCountByInterviewID =
        aiInsightGenerationProgressState[artifactType][
          `${interviewID}-insightCount`
        ];

      if (insightCountByInterviewID > 0) {
        alertByProgress = (
          <IwAlert
            severity="info"
            icon={<CelebrationRoundedIcon />}
            onClose={() => onCloseProgressAlert()}
          >
            <AlertTitle>
              {insightCountByInterviewID} Insights Generated
            </AlertTitle>
            Our AI has successfully created {insightCountByInterviewID} insights
            based on the transcript.
          </IwAlert>
        );
      } else {
        alertByProgress = (
          <IwAlert
            severity="info"
            icon={<CelebrationRoundedIcon />}
            onClose={() => onCloseProgressAlert()}
          >
            <AlertTitle>No insight found</AlertTitle>
            Our AI has found {insightCountByInterviewID} insights based on the
            transcript.
          </IwAlert>
        );
      }
    } else if (
      progressByInterviewID === AI_GENERATION_COMPLETION_STATUS.IN_PROGRESS
    ) {
      alertByProgress = (
        <IwAlert severity="info" icon={<AutoAwesomeRoundedIcon />}>
          <AlertTitle>Insight Generation in progress</AlertTitle>
          Our AI is currently generating useful insights from the transcript.
          You can see them here once the process is done.
          <LinearProgress sx={{ marginTop: "16px" }} />
        </IwAlert>
      );
    } else if (isFetchingTriggered) {
      alertByProgress = (
        <IwAlert severity="info" icon={<CircularProgress size={16} />}>
          <AlertTitle>Fetching Insights</AlertTitle>
          Fetching the newly generated insights. Please wait.
          <LinearProgress sx={{ marginTop: "16px" }} />
        </IwAlert>
      );
    } else if (
      progressByInterviewID === AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
    ) {
      alertByProgress = (
        <IwAlert
          severity="success"
          icon={<CelebrationRoundedIcon />}
          onClose={() => onCloseProgressAlert()}
        >
          <AlertTitle>Insights Generated</AlertTitle>
          Our AI has successfully created insights based on the transcript.
        </IwAlert>
      );
    } else if (
      progressByInterviewID === AI_GENERATION_COMPLETION_STATUS.FAILED
    ) {
      alertByProgress = (
        <IwAlert
          severity="error"
          icon={<ErrorOutlineRoundedIcon />}
          onClose={() => onCloseProgressAlert()}
        >
          <AlertTitle>Insight Generation Failed</AlertTitle>
          Something went wrong.
        </IwAlert>
      );
    }
    return <Box mt={1}>{alertByProgress}</Box>;
  };

  const handleSelectInsight = async (insight) =>{
    let quoteValue;
    try {
      quoteValue = JSON.parse(insight?.quote);
    } catch (e) {
      quoteValue = null;
    }

    const result = quoteValue?.source === "note" ? "notes" : quoteValue?.source !== "transcript" && quoteValue?.source !== "note" ? "metaanalysis" : undefined
    if(result){
       await setSelectedArtifactType(setBase, result);
    }
    
    selectInsight(setBase, insight);
  }

  return (
    <>
      {isFindingLoading || isBaseLoading ? (
        <InsightDetailsSkeletion/>
      ) : (
        <Box
          // component={Paper}
          sx={{
            //border: "1px solid #c2c2c2",
            flexGrow: 1,
            overflowY: "scroll",
          }}
        >
          <InsightArtifactTypeToggle
            handleToggle={handleToggle}
            selectedTab={selectedTab}
            insight={`Insight (${sortedInsights?.length})`}
            style={{
              padding: "8px 12px",
              borderRadius:"3px",
              position: "sticky",
              top: 0,
              zIndex: 1,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)"
            }}
            component={"insight"}
          />

          {renderInsightProgressAlert() !== null &&
            renderInsightProgressAlert()}

          {sortedInsights?.map((insight, i) => {
            const isSelected = selectedFinding === insight?.findingID;

            const childInsights = (insight?.childFindingIDs || [])
              .map((id) => findings?.[id])
              .filter(Boolean)
              .map((child) => ({
                id: child.findingID,
                artifactID: child.artifactID,
                name: child.name,
                quote: child.quote,
                createdBy: child.owner,
                creatorID: child.userID,
                audioBegin: parseFloat(child.audioBegin),
                audioEnd: parseFloat(child.audioEnd),
                isDraft: child.isDraft,
                user: child.user,
                onInsightClick: () => {
                  selectInsight(setBase, child);
                },
              }));

            return (
              <InsightDetailsRow
                key={insight.findingID}
                id={insight.findingID}
                artifactID={insight.artifactID}
                name={insight.name}
                quote={insight.quote}
                createdBy={insight.owner}
                creatorID={insight.userID}
                audioBegin={parseFloat(insight.audioBegin)}
                audioEnd={parseFloat(insight.audioEnd)}
                isDraft={insight.isDraft}
                isSelected={isSelected}
                user={insight.user}
                onInsightClick={() => {
                  if (!isSelected) {
                    handleSelectInsight(insight);
                  }
                }}
                onAccept={(onSuccess, onFail) => onAccept(insight, onSuccess, onFail)}
                childInsights={childInsights} 
                metaArtifactData = {customMetaArtifactData || metaArtifactData}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};

export default InsightDetails;
